import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import logger from "../utils/logger";
import { FileText, Download, Calendar, ChevronRight, BarChart3, Shield, Users, AlertTriangle } from "lucide-react";

const API = `${window.location.origin}/api`;

const CATEGORY_ICONS = {
  compliance: { icon: Shield, color: "#2563eb" },
  risk: { icon: AlertTriangle, color: "#ef4444" },
  screening: { icon: BarChart3, color: "#FF6B35" },
  kyc: { icon: Users, color: "#10b981" },
};

export default function ReportsPage() {
  const [reportTypes, setReportTypes] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().split("T")[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split("T")[0]);
  const [generating, setGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [typesRes, histRes] = await Promise.all([
        axios.get(`${API}/reports/types`, { withCredentials: true }),
        axios.get(`${API}/reports/history`, { withCredentials: true }),
      ]);
      setReportTypes(typesRes.data.reports || []);
      setHistory(histRes.data.reports || []);
    } catch (err) { logger.error("Failed to load reports:", err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    if (!selectedReport) return;
    setGenerating(true);
    setGeneratedReport(null);
    try {
      const { data } = await axios.post(`${API}/reports/generate`, {
        report_type: selectedReport, date_from: dateFrom, date_to: dateTo,
      }, { withCredentials: true });
      setGeneratedReport(data);
      fetchData();
    } catch (err) { logger.error("Report generation failed:", err); }
    finally { setGenerating(false); }
  };

  const inputStyle = {
    background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px",
    padding: "9px 12px", color: "#f1f5f9", fontSize: "13px", outline: "none",
  };

  return (
    <div data-testid="reports-page">
      <div className="mb-6">
        <h1 style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.5px", color: "#f1f5f9", marginBottom: "4px" }} data-testid="reports-title">
          Reports
        </h1>
        <p style={{ color: "#94a3b8", fontSize: "14px" }}>Generate compliance reports for RBI, FIU-IND, and internal audit</p>
      </div>

      {/* Report Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {reportTypes.map(rt => {
          const cat = CATEGORY_ICONS[rt.category] || CATEGORY_ICONS.compliance;
          const CIcon = cat.icon;
          const isSelected = selectedReport === rt.id;
          return (
            <button key={rt.id} data-testid={`report-type-${rt.id}`}
              onClick={() => setSelectedReport(isSelected ? null : rt.id)}
              style={{
                background: isSelected ? "rgba(37,99,235,0.08)" : "#0d1117",
                border: `1px solid ${isSelected ? "#2563eb" : "#1e2530"}`,
                borderRadius: "10px", padding: "16px", cursor: "pointer",
                textAlign: "left", transition: "all 0.2s",
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div style={{
                  width: "32px", height: "32px", borderRadius: "8px",
                  background: `${cat.color}15`, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <CIcon style={{ width: "15px", height: "15px", color: cat.color }} />
                </div>
                <h3 style={{ fontSize: "13px", fontWeight: 600, color: "#f1f5f9" }}>{rt.name}</h3>
              </div>
              <p style={{ fontSize: "11px", color: "#475569", lineHeight: 1.5 }}>{rt.description}</p>
            </button>
          );
        })}
      </div>

      {/* Generate Controls */}
      {selectedReport && (
        <div className="card-aml mb-6" data-testid="report-generate-panel">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <label style={{ fontSize: "10px", fontWeight: 600, color: "#475569", display: "block", marginBottom: "4px" }}>FROM</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} data-testid="report-date-from" />
            </div>
            <div>
              <label style={{ fontSize: "10px", fontWeight: 600, color: "#475569", display: "block", marginBottom: "4px" }}>TO</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} data-testid="report-date-to" />
            </div>
            <button onClick={handleGenerate} disabled={generating} data-testid="generate-report-btn" style={{
              background: generating ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", padding: "10px 24px", cursor: generating ? "not-allowed" : "pointer",
              color: "#fff", fontSize: "13px", fontWeight: 600, marginTop: "16px",
              display: "flex", alignItems: "center", gap: "6px",
            }}>
              <FileText style={{ width: "14px", height: "14px" }} />
              {generating ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
      )}

      {/* Generated Report Display */}
      {generatedReport && (
        <div className="card-aml mb-6" data-testid="generated-report">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#f1f5f9" }}>{generatedReport.report_name}</h3>
              <p style={{ fontSize: "11px", color: "#475569" }}>
                {generatedReport.date_from} to {generatedReport.date_to} | Generated: {new Date(generatedReport.created_at).toLocaleString("en-IN")}
              </p>
            </div>
            <span style={{ fontSize: "9px", fontWeight: 700, padding: "3px 10px", borderRadius: "4px", background: "rgba(16,185,129,0.12)", color: "#10b981", textTransform: "uppercase" }}>
              Generated
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(generatedReport.data || {}).map(([key, value]) => (
              <div key={key} style={{ padding: "12px", background: "#080c12", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "20px", fontWeight: 700, color: "#f1f5f9" }}>
                  {typeof value === "number" ? (value % 1 === 0 ? value : value.toFixed(1)) : value}
                </div>
                <div style={{ fontSize: "9px", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "2px" }}>
                  {key.replace(/_/g, " ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report History */}
      {history.length > 0 && (
        <div className="card-aml" data-testid="report-history" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #1e2530" }}>
            <div className="flex items-center gap-2">
              <Calendar style={{ width: "14px", height: "14px", color: "#475569" }} />
              <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#f1f5f9" }}>Report History</h3>
            </div>
          </div>
          {history.map(r => (
            <div key={r.id} style={{
              padding: "12px 20px", borderTop: "1px solid #0f1520",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#f1f5f9" }}>{r.report_name}</div>
                <div style={{ fontSize: "10px", color: "#475569" }}>
                  {r.date_from} to {r.date_to} | by {r.created_by_name}
                </div>
              </div>
              <div style={{ fontSize: "11px", color: "#475569" }}>
                {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
